import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Team } from "../../models/Team";
import { TeamSubscription } from "../../models/TeamTier";
import { fetchUniqueTeamsByTeamName } from "../../services/TeamService";
import {
    getTeamTiersForAdmin,
    addTeamTier,
    updateTeamTier,
    deleteTeamTier,
} from "../../services/TeamTierService";

const card: React.CSSProperties = {
    background: "linear-gradient(135deg, rgba(26,26,26,0.95) 0%, rgba(37,37,37,0.95) 100%)",
    border: "1px solid rgba(255,102,0,0.15)",
    borderRadius: 12,
    padding: "20px 22px",
};

const inputStyle: React.CSSProperties = {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,102,0,0.2)",
    borderRadius: 6,
    color: "rgba(255,255,255,0.9)",
    padding: "8px 12px",
};

const label: React.CSSProperties = {
    display: "block",
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "rgba(255,255,255,0.55)",
    marginBottom: 4,
};

const th: React.CSSProperties = {
    padding: "10px 16px",
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "rgba(255,255,255,0.55)",
    textAlign: "left",
};

const td: React.CSSProperties = { padding: "10px 16px", color: "rgba(255,255,255,0.85)" };

// Amounts are whole kroner. parseInt would silently turn "1.000" into 1.
const parseAmount = (raw: string): number | null => {
    const trimmed = raw.trim();
    if (!/^\d+$/.test(trimmed)) return null;
    const value = Number(trimmed);
    return value > 0 ? value : null;
};

/**
 * Admin management for the team donator tier. Unlike the donations table above, a tier is one row
 * per team rather than a ledger — a second donation from the same org tops up the amount.
 */
const TeamTierSection: React.FC = () => {
    const [tiers, setTiers] = useState<TeamSubscription[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [showAdd, setShowAdd] = useState(false);

    // Add form
    const [search, setSearch] = useState("");
    const [results, setResults] = useState<Team[]>([]);
    const [searching, setSearching] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
    const [amount, setAmount] = useState("350");
    const [payerSteamId, setPayerSteamId] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            setTiers(await getTeamTiersForAdmin());
        } catch {
            setError("Could not load team tiers.");
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const runSearch = async () => {
        if (search.trim().length < 2) return;
        setSearching(true);
        try {
            setResults(await fetchUniqueTeamsByTeamName(search.trim()));
        } catch {
            setResults([]);
        } finally {
            setSearching(false);
        }
    };

    const resetForm = () => {
        setSearch(""); setResults([]); setSelectedTeam(null);
        setAmount("350"); setPayerSteamId(""); setNote("");
    };

    const submitAdd = async () => {
        setError(null); setNotice(null);
        if (!selectedTeam) { setError("Pick a team first."); return; }
        const parsed = parseAmount(amount);
        if (parsed === null) { setError("Amount must be a whole number of kroner."); return; }

        setSaving(true);
        try {
            const { coveredPlayers } = await addTeamTier({
                teamId: selectedTeam.teamId,
                amount: parsed,
                purchasedBySteamId: payerSteamId.trim() || null,
                note: note.trim() || null,
            });
            setNotice(`${selectedTeam.teamName} now covers ${coveredPlayers} player(s).`);
            resetForm();
            setShowAdd(false);
            await load();
        } catch (e: any) {
            setError(e?.response?.data ?? "Could not add the team tier.");
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (tier: TeamSubscription) => {
        setError(null);
        try {
            await updateTeamTier(tier.id, {
                teamId: tier.teamId,
                amount: tier.amount,
                purchasedBySteamId: tier.purchasedBySteamId,
                note: tier.note,
                active: !tier.active,
            });
            await load();
        } catch {
            setError("Could not update the team tier.");
        }
    };

    const remove = async (tier: TeamSubscription) => {
        if (!window.confirm(`Delete the team tier for ${tier.teamName}? Their perks stop immediately.`)) return;
        setError(null);
        try {
            await deleteTeamTier(tier.id);
            await load();
        } catch {
            setError("Could not delete the team tier.");
        }
    };

    return (
        <div style={{ marginTop: 42 }}>
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                <div>
                    <h2 style={{ color: "#e2e8f0", fontWeight: 700, fontSize: "1.4rem" }}>Team tiers</h2>
                    <p style={{ color: "rgba(255,255,255,0.6)", marginBottom: 8 }}>
                        One donation covering a whole roster. Never expires — the covered players are
                        always the team's current-season roster, plus the coach and owner.
                    </p>
                </div>
                <button
                    className="btn btn-warning text-dark fw-bold"
                    onClick={() => { setShowAdd((s) => !s); setError(null); }}
                    style={{ height: "fit-content" }}
                >
                    {showAdd ? "Close" : "+ Add team tier"}
                </button>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}
            {notice && <div className="alert alert-success" onClick={() => setNotice(null)} style={{ cursor: "pointer" }}>{notice}</div>}

            {showAdd && (
                <div style={{ ...card, margin: "8px 0 20px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
                        <div style={{ gridColumn: "1 / -1" }}>
                            <label style={label}>Team *</label>
                            {selectedTeam ? (
                                <div className="d-flex align-items-center gap-2">
                                    <span style={{ color: "#ff9933", fontWeight: 700 }}>{selectedTeam.teamName}</span>
                                    <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.8rem" }}>{selectedTeam.teamId}</span>
                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedTeam(null)}>Change</button>
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex gap-2">
                                        <input
                                            style={{ ...inputStyle, flex: 1 }}
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(); } }}
                                            placeholder="Search team name…"
                                        />
                                        <button className="btn btn-outline-warning" onClick={runSearch} disabled={searching}>
                                            {searching ? "…" : "Search"}
                                        </button>
                                    </div>
                                    {results.length > 0 && (
                                        <div style={{ marginTop: 8, maxHeight: 180, overflowY: "auto" }}>
                                            {results.map((team) => (
                                                <button
                                                    key={`${team.teamId}-${team.season}`}
                                                    className="btn btn-sm btn-outline-light w-100 text-start mb-1"
                                                    onClick={() => { setSelectedTeam(team); setResults([]); }}
                                                >
                                                    {team.teamName}
                                                    <span style={{ color: "rgba(255,255,255,0.4)", marginLeft: 8, fontSize: "0.78rem" }}>
                                                        s{team.season} · {team.teamId}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <div>
                            <label style={label}>Amount (kr) *</label>
                            <input style={{ ...inputStyle, width: "100%" }} type="number" min={1} value={amount}
                                onChange={(e) => setAmount(e.target.value)} placeholder="350" />
                        </div>
                        <div>
                            <label style={label}>Paid by SteamID (optional)</label>
                            <input style={{ ...inputStyle, width: "100%" }} value={payerSteamId}
                                onChange={(e) => setPayerSteamId(e.target.value)} placeholder="7656119…" />
                        </div>
                        <div>
                            <label style={label}>Note (optional)</label>
                            <input style={{ ...inputStyle, width: "100%" }} value={note}
                                onChange={(e) => setNote(e.target.value)} placeholder="MobilePay ref, contact…" />
                        </div>
                    </div>
                    <div className="mt-3 d-flex gap-2">
                        <button className="btn btn-warning text-dark fw-bold" onClick={submitAdd} disabled={saving}>
                            {saving ? "Saving…" : "Add team tier"}
                        </button>
                        <button className="btn btn-outline-secondary" onClick={() => { resetForm(); setShowAdd(false); }}>Cancel</button>
                    </div>
                </div>
            )}

            <div style={{ ...card, padding: 0, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
                    <thead>
                        <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                            <th style={th}>Team</th>
                            <th style={th}>Amount</th>
                            <th style={th}>Bought</th>
                            <th style={th}>Note</th>
                            <th style={{ ...th, textAlign: "right" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(tiers ?? []).map((tier) => (
                            <tr key={tier.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)", opacity: tier.active ? 1 : 0.5 }}>
                                <td style={td}>
                                    <Link to={`/team/${tier.teamId}`} style={{ color: "#ff9933", textDecoration: "none", fontWeight: 600 }}>
                                        {tier.teamName}
                                    </Link>
                                    {!tier.active && <span style={{ marginLeft: 8, fontSize: "0.72rem", color: "rgba(255,255,255,0.5)" }}>INACTIVE</span>}
                                </td>
                                <td style={td}>{tier.amount} kr</td>
                                <td style={td}>
                                    s{tier.season}
                                    <span style={{ color: "rgba(255,255,255,0.4)", marginLeft: 6, fontSize: "0.8rem" }}>
                                        {tier.createdAt ? new Date(tier.createdAt).toLocaleDateString() : "—"}
                                    </span>
                                </td>
                                <td style={{ ...td, color: "rgba(255,255,255,0.6)" }}>{tier.note ?? "—"}</td>
                                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                                    <button className="btn btn-sm btn-outline-warning me-2" onClick={() => toggleActive(tier)}>
                                        {tier.active ? "Deactivate" : "Activate"}
                                    </button>
                                    <button className="btn btn-sm btn-outline-danger" onClick={() => remove(tier)}>Delete</button>
                                </td>
                            </tr>
                        ))}
                        {tiers !== null && tiers.length === 0 && (
                            <tr><td colSpan={5} style={{ padding: 24, textAlign: "center", color: "rgba(255,255,255,0.5)" }}>No team tiers yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TeamTierSection;
